import React, { useEffect, useState } from 'react';
import { useAccount, useBalance } from 'wagmi';
import { formatUnits } from 'viem';
import { Wallet, Copy, ExternalLink, CheckCircle } from 'lucide-react';
import { colors, spacing, typography, borders } from '../../tokens';
import { formatAddress } from '../../web3/utils/blockchain';
import { getChainInfo, getExplorerAddressUrl } from '../../web3/config';

export interface WalletInfoProps {
  /**
   * Show wallet balance
   */
  showBalance?: boolean;
  /**
   * Show chain information
   */
  showChain?: boolean;
  /**
   * Show copy address button
   */
  showCopyButton?: boolean;
  /**
   * Show explorer link
   */
  showExplorerLink?: boolean;
  /**
   * Card style or inline style
   */
  variant?: 'card' | 'inline';
}

/**
 * WalletInfo - Display Wallet Information
 *
 * Features:
 * - Display wallet address
 * - Show balance
 * - Show current chain
 * - Copy address to clipboard
 * - Link to block explorer
 *
 * Usage:
 * ```tsx
 * <WalletInfo
 *   showBalance
 *   showChain
 *   showCopyButton
 *   showExplorerLink
 *   variant="card"
 * />
 * ```
 */
export const WalletInfo: React.FC<WalletInfoProps> = ({
  showBalance = true,
  showChain = true,
  showCopyButton = true,
  showExplorerLink = true,
  variant = 'card',
}) => {
  const { address, chainId, isConnected } = useAccount();
  const { data: balance } = useBalance({
    address,
  });

  const [copied, setCopied] = useState(false);

  const chainInfo = chainId ? getChainInfo(chainId) : null;
  const explorerUrl = chainId && address ? getExplorerAddressUrl(chainId, address) : '';

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  // Copy address to clipboard
  const handleCopy = async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
    } catch (error) {
      console.error('Failed to copy address:', error);
    }
  };

  // Open explorer in new tab
  const handleExplorer = () => {
    if (explorerUrl) {
      window.open(explorerUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (!isConnected || !address) {
    return null;
  }

  // Card variant styles
  const cardStyles: React.CSSProperties = {
    backgroundColor: colors.background.primary.default,
    border: `1px solid ${colors.border.default.default}`,
    borderRadius: borders.component.default.radius,
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  };

  // Inline variant styles
  const inlineStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
    flexWrap: 'wrap',
  };

  const containerStyles = variant === 'card' ? cardStyles : inlineStyles;

  // Row styles
  const rowStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.text.primary.default,
  };

  // Label styles
  const labelStyles: React.CSSProperties = {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.text.muted.default,
  };

  // Value styles
  const valueStyles: React.CSSProperties = {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text.primary.default,
    fontFamily: typography.fontFamily.mono,
  };

  // Button styles
  const buttonStyles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    backgroundColor: 'transparent',
    border: `1px solid ${colors.border.default.default}`,
    borderRadius: borders.component.default.radius,
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  return (
    <div style={containerStyles}>
      {/* Address Row */}
      <div style={rowStyles}>
        <Wallet size={20} color={colors.brand.primary.base} />
        <div style={{ flex: 1 }}>
          {variant === 'card' && <div style={labelStyles}>Address</div>}
          <div style={valueStyles}>{formatAddress(address)}</div>
        </div>
        {showCopyButton && (
          <button
            style={buttonStyles}
            onClick={handleCopy}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.background.secondary.default;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            title="Copy address"
          >
            {copied ? (
              <CheckCircle size={16} color={colors.semantic.success} />
            ) : (
              <Copy size={16} color={colors.text.muted.default} />
            )}
          </button>
        )}
        {showExplorerLink && explorerUrl && (
          <button
            style={buttonStyles}
            onClick={handleExplorer}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.background.secondary.default;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            title="View on explorer"
          >
            <ExternalLink size={16} color={colors.text.muted.default} />
          </button>
        )}
      </div>

      {/* Balance Row */}
      {showBalance && balance && (
        <div style={rowStyles}>
          <div style={{ flex: 1 }}>
            {variant === 'card' && <div style={labelStyles}>Balance</div>}
            <div style={valueStyles}>
              {parseFloat(formatUnits(balance.value, balance.decimals)).toFixed(4)} {balance.symbol}
            </div>
          </div>
        </div>
      )}

      {/* Chain Row */}
      {showChain && chainInfo && (
        <div style={rowStyles}>
          <div style={{ flex: 1 }}>
            {variant === 'card' && <div style={labelStyles}>Network</div>}
            <div style={{
              ...valueStyles,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.xs,
            }}>
              <span>{chainInfo.name}</span>
              {chainInfo.type === 'testnet' && (
                <span style={{
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.medium,
                  color: colors.semantic.warning,
                  backgroundColor: `${colors.semantic.warning}20`,
                  padding: `2px ${spacing.xs}`,
                  borderRadius: borders.component.default.radius,
                }}>
                  Testnet
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletInfo;
